import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useT } from "@/i18n";
import { usePageMeta } from "@/lib/format";
import { PageHeader } from "@/components/StatCard";
import Seo from "@/components/Seo";
import UploadField from "@/components/UploadField";
import { fileUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ChevronLeft, Plus, Users2, ChevronRight } from "lucide-react";

const STATUS_COLORS = {
  draft: "bg-amber-100 text-amber-700",
  active: "bg-emerald-100 text-emerald-700",
  archived: "bg-secondary text-muted-foreground",
};

function CreateDialog({ t, onClose, onCreated }) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [cover, setCover] = useState("");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (name.trim().length < 2) { toast.error(t("col2.err_name")); return; }
    setSaving(true);
    try { const { data } = await api.post("/collectives", { name: name.trim(), description: desc.trim(), cover_image_url: cover || null }); toast.success(t("col2.created")); onCreated(data); }
    catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="col2-create-dialog">
        <DialogHeader>
          <DialogTitle>{t("col2.new")}</DialogTitle>
          <DialogDescription>{t("col2.new_desc")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("col2.f_name")}</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("col2.f_name_ph")} data-testid="col2-name" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("col2.f_desc")}</label>
            <Textarea className="min-h-[80px]" value={desc} onChange={(e) => setDesc(e.target.value)} placeholder={t("col2.f_desc_ph")} data-testid="col2-desc" />
          </div>
          <div>
            <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-muted-foreground">{t("col2.f_cover")}</label>
            <UploadField value={cover} onChange={setCover} kind="photo" accept="image/*" testId="col2-cover" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" className="rounded-full" onClick={onClose}>{t("common.cancel")}</Button>
          <Button className="rounded-full" onClick={save} disabled={saving} data-testid="col2-save">{saving ? t("common.saving") : t("common.save")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Collectives() {
  const { user } = useAuth();
  const t = useT();
  const navigate = useNavigate();
  usePageMeta(t("col2.meta_title"));
  const [rows, setRows] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/collectives/mine"); setRows(Array.isArray(data) ? data : []); } catch { setRows([]); }
  }, []);
  useEffect(() => { if (user?.role === "coach") load(); }, [user, load]);

  if (user && user.role !== "coach") return <Navigate to="/app/circles" replace />;

  return (
    <div data-testid="coach-collectives-page">
      <Seo title={t("col2.seo")} noindex />
      <Link to="/app/coach" className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="col2-back">
        <ChevronLeft className="h-4 w-4" /> {t("cset.back_dashboard")}
      </Link>
      <PageHeader eyebrow={t("col2.eyebrow")} title={t("col2.title")} subtitle={t("col2.subtitle")}
        action={<Button className="gap-1.5 rounded-full" onClick={() => setCreating(true)} data-testid="col2-new-btn"><Plus className="h-4 w-4" /> {t("col2.new")}</Button>} />

      {rows === null ? (
        <div className="space-y-2">{[0, 1].map((i) => <Skeleton key={i} className="h-24 rounded-2xl" />)}</div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center" data-testid="col2-empty">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground"><Users2 className="h-6 w-6" /></div>
          <p className="mt-4 font-display text-lg text-foreground">{t("col2.empty_title")}</p>
          <p className="mt-1 text-sm text-muted-foreground">{t("col2.empty_sub")}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((c) => (
            <button key={c.id} onClick={() => navigate(`/app/coach/collectives/${c.id}`)} data-testid={`col2-item-${c.id}`} className="flex w-full items-center gap-3 rounded-2xl border border-border bg-card p-3 text-left hover:bg-accent/40">
              {c.cover_image_url ? <img src={fileUrl(c.cover_image_url)} alt="" className="h-14 w-14 shrink-0 rounded-xl object-cover" /> : <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary"><Users2 className="h-6 w-6" /></div>}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <p className="truncate font-semibold text-foreground">{c.name}</p>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold ${STATUS_COLORS[c.status] || ""}`}>{t(`col2.status_${c.status}`)}</span>
                </div>
                <p className="mt-0.5 truncate text-xs text-muted-foreground">{t("col2.members_n", { n: (c.members || []).length })}{c.my_role === "owner" ? ` · ${t("col2.owner")}` : ""}</p>
              </div>
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
            </button>
          ))}
        </div>
      )}

      {creating && <CreateDialog t={t} onClose={() => setCreating(false)} onCreated={(c) => { setCreating(false); if (c?.id) navigate(`/app/coach/collectives/${c.id}`); else load(); }} />}
    </div>
  );
}
